<script lang="ts">
import { defineComponent, ref, computed, h } from 'vue';
import { validateOne } from '../../_utils/validators';
import PtIcon from '../../icon';

type Type = 'text' | 'outline' | 'contain';
type Color = 'blue' | 'green' | 'darkgreen' | 'red' | 'black' | 'purple' | 'whiteness' | 'grey' | 'darkblue' | 'orange';
type Size = 'large' | 'medium' | 'normal' | 'small';
type HTMLType = 'button' | 'submit' | 'reset';
type EmitFn = (evt: Event) => void;
interface IconConfig {
    name: string;
    color?: string;
    width?: string;
    height?: string;
    size?: string;
}

export interface Props {
    type: Type;
    size: Size;
    color: Color;
    loading?: boolean | 'before' | 'after' | 'cover';
    disabled?: boolean;
    reverse?: boolean;
    fullfilled?: boolean;
    block?: boolean;
    ghost?: boolean;
    htmlType: HTMLType;
    href?: string | boolean;
    target?: HTMLAnchorElement['target'];
    icon?: String | IconConfig;
    iconAfter?: String | IconConfig;
}

const props = {
    type: {
        type: String as () => Props['type'],
        default: 'contain',
        validator: validateOne(['text', 'outline', 'contain'])
    },
    size: {
        type: String as () => Props['size'],
        default: 'normal',
        validator: validateOne(['large', 'medium', 'normal', 'small'])
    },
    color: {
        type: String as () => Props['color'],
        default: 'blue',
        validator: validateOne([
            'blue',
            'green',
            'darkgreen',
            'red',
            'black',
            'purple',
            'white',
            'grey',
            'darkblue',
            'orange'
        ])
    },
    reverse: {
        type: Boolean,
        default: false
    },
    ghost: {
        type: Boolean,
        default: false
    },
    block: {
        type: Boolean,
        default: false
    },
    fullfilled: {
        type: Boolean,
        default: false
    },
    htmlType: {
        type: String as unknown as () => Props['htmlType'],
        default: 'button',
        validator: validateOne(['button', 'submit', 'reset'])
    },
    loading: {
        type: [Boolean, String] as unknown as () => Props['loading'],
        default: false,
        validator: validateOne(['before', 'after', 'cover', false, true])
    },
    disabled: {
        type: Boolean,
        default: false
    },
    href: [String, Boolean] as unknown as () => Props['href'],
    target: String,
    icon: [String, Object] as unknown as () => Props['iconAfter'],
    iconAfter: [String, Object] as unknown as () => Props['iconAfter'],
    onClick: Function
};

export default defineComponent({
    name: 'PtButton',
    props,
    emits: ['click'],
    setup(props: Props, { emit, slots }) {
        const buttonRef = ref<HTMLButtonElement>();
        const handleClick: EmitFn = evt => {
            emit('click', evt);
            if (buttonRef.value) buttonRef.value?.blur();
        };

        const hasIconBefore = computed(() => Boolean(props.icon || slots.icon));
        const hasIconAfter = computed(() => !(props.icon || slots.icon) && Boolean(props.iconAfter || slots.iconAfter));
        const icon = computed(() => props.icon || props.iconAfter);
        const iconProps = computed(() => ({
            icon: (icon.value as IconConfig)?.name ?? icon.value,
            style:
                typeof icon.value !== 'object'
                    ? undefined
                    : {
                          ...(icon.value as IconConfig),
                          ...{
                              width:
                                  (icon.value as IconConfig)?.size ??
                                  (icon.value as IconConfig)?.width ??
                                  (props.icon as IconConfig)?.height ??
                                  '16px',
                              height:
                                  (icon.value as IconConfig)?.size ??
                                  (icon.value as IconConfig)?.height ??
                                  (props.icon as IconConfig)?.width ??
                                  '16px'
                          }
                      }
        }));

        const btnProps = computed(() => ({
            class: [
                'pt-button',
                `pt-button--${props.type}--${props.color}${props.reverse ? '--reverse' : ''}`,
                `pt-button--${props.size}`,
                {
                    'pt-button--ghost': props.ghost,
                    'pt-button--block': props.block,
                    'pt-button--fullfilled': props.fullfilled,
                    'is-disabled': Boolean(props.loading) || props.disabled,
                    'is-loading-before': props.loading === 'before' || (props.loading === true && !hasIconAfter.value),
                    'is-loading-after': props.loading === 'after' || (props.loading === true && hasIconAfter.value),
                    'is-loading-cover': props.loading === 'cover'
                }
            ],
            disabled: props.loading || props.disabled,
            type: props.htmlType,
            href: typeof props.href === 'string' && props.href !== '' ? props.href : undefined,
            target: props.href ? props.target : undefined,
            onClick: handleClick,
            ref: buttonRef
        }));

        return () =>
            h(btnProps.value.href ? 'a' : 'button', btnProps.value, [
                hasIconBefore.value
                    ? h('span', { class: 'pt-button--icon-wrapper' }, [
                          icon.value ? h(PtIcon, iconProps.value) : null,
                          slots?.icon?.()
                      ])
                    : null,
                slots.default ? h('span', { class: 'pt-button--content' }, slots.default()) : null,
                hasIconAfter.value
                    ? h('span', { class: 'pt-button--icon-wrapper' }, [
                          icon.value ? h(PtIcon, iconProps.value) : null,
                          slots?.iconAfter?.()
                      ])
                    : null
            ]);
    }
});
</script>
